import React from 'react';

import { faCalendar, faCloudArrowUp } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Popover, PopoverContent, PopoverTrigger, PopoverPortal } from '@radix-ui/react-popover';
import { useIntl } from 'react-intl';

import { cashCollectionsMessages } from 'src/features/cashCollections/utils/messages';
import { parkingManagementMessages } from 'src/features/parkingManagement/utils';
import { useListAllParkings } from 'src/features/payStations/api/useListAllParkings';
import { useListPayStations } from 'src/features/payStations/api/useListPaystations';
import payStationsMessages from 'src/features/payStations/utils/messages';
import {
    FormFieldBlockLabel,
    FormFieldBlockName,
    FormFieldBlockTitle,
} from 'src/registry/components/blocks/FormFieldBlock';
import { GeneralBlock, GeneralBlockBody } from 'src/registry/components/blocks/GeneralBlock';
import { Button } from 'src/registry/ui/Button';
import { Calendar } from 'src/registry/ui/Calendar';
import { DialogSeparator } from 'src/registry/ui/Dialog';
import { FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { Input } from 'src/registry/ui/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { Textarea } from 'src/registry/ui/Textarea';
import { generalMessages } from 'src/utils/messages';

import type { UseFormReturn } from 'react-hook-form';
import type { CreateCashCollectionFormSchemaValues } from 'src/features/cashCollections/hooks/useCreateCashCollection';

type CollectionDetailsProps = {
    form: UseFormReturn<CreateCashCollectionFormSchemaValues>;
    isEditModal: boolean;
};

type FileFieldProps<T extends keyof CreateCashCollectionFormSchemaValues> = {
    fieldName: T;
    fieldLabel: { id: string; defaultMessage: string };
};

const CollectionDetails = ({ form, isEditModal }: CollectionDetailsProps) => {
    const { formatMessage, formatDate } = useIntl();
    const [openCalendar, setOpenCalendar] = React.useState<boolean>(false);
    const [uploadedFiles, setUploadedFiles] = React.useState<Record<string, File[]>>({});

    const { data: parkingsData } = useListAllParkings({
        enabled_cash_collection: true,
    });

    const selectedParkingId = form.watch('parking');

    const { data: payStationsData } = useListPayStations({
        parking: selectedParkingId,
    });

    const statusTranslationMap = {
        paid: formatMessage(cashCollectionsMessages.cashCollectionsPaid),
        collected: formatMessage(cashCollectionsMessages.cashCollectionsCollected),
    };

    const cashCollectionTypeTranslationMap = {
        collect: formatMessage(cashCollectionsMessages.cashCollectionCollect),
        load: formatMessage(cashCollectionsMessages.cashCollectionLoad),
    };

    const hideStatusFormField = form.watch('type') === 'collect' || form.watch('type') === undefined;

    const handleFileChange = (
        e: React.ChangeEvent<HTMLInputElement>,
        fieldName: keyof CreateCashCollectionFormSchemaValues,
    ) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;

        const filesArray = Array.from(files);

        form.setValue(fieldName, filesArray, {
            shouldValidate: true,
            shouldDirty: true,
        });

        setUploadedFiles((prev) => ({
            ...prev,
            [fieldName]: filesArray,
        }));
    };

    const getFileUploadFormFieldBlock = <T extends keyof CreateCashCollectionFormSchemaValues>({
        fieldName,
        fieldLabel,
    }: FileFieldProps<T>) => {
        return (
            <FormField
                control={form.control}
                name={fieldName}
                render={() => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(fieldLabel)}</FormLabel>
                        <FormControl>
                            <label htmlFor={fieldName} className="w-full cursor-pointer">
                                <GeneralBlock>
                                    <GeneralBlockBody className="text-primary-700 flex w-full items-center justify-center font-bold">
                                        <div className="flex flex-col items-center">
                                            <FontAwesomeIcon
                                                icon={faCloudArrowUp}
                                                size="lg"
                                                className="text-primary-700"
                                            />
                                            <span>{formatMessage(parkingManagementMessages.clickToUpload)}</span>

                                            {uploadedFiles[fieldName]?.length > 0 && (
                                                <div className="mt-1 flex flex-col gap-1">
                                                    {uploadedFiles[fieldName].map((file, idx) => (
                                                        <span key={idx} className="text-sm font-semibold text-gray-500">
                                                            {file.name}
                                                        </span>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <input
                                            type="file"
                                            id={fieldName}
                                            className="hidden"
                                            multiple
                                            onChange={(e) => handleFileChange(e, fieldName)}
                                        />
                                    </GeneralBlockBody>
                                </GeneralBlock>
                            </label>
                        </FormControl>
                        <FormMessage />
                    </FormItem>
                )}
            />
        );
    };

    return (
        <>
            <FormFieldBlockLabel>
                <FormFieldBlockTitle>
                    <FormFieldBlockName>{formatMessage(cashCollectionsMessages.collectionDetails)}</FormFieldBlockName>
                </FormFieldBlockTitle>
            </FormFieldBlockLabel>
            <DialogSeparator />
            <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(cashCollectionsMessages.cashCollectionType)}
                        </FormLabel>
                        <FormControlStack className="mr-3">
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value} disabled={isEditModal}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={formatMessage(generalMessages.selectValue)} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(cashCollectionTypeTranslationMap).map(([key, value]) => (
                                            <SelectItem key={key} value={key}>
                                                {value}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="collected_by"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(cashCollectionsMessages.collectorName)}
                        </FormLabel>
                        <FormControlStack className="mr-3">
                            <FormControl>
                                <Input type="text" placeholder={formatMessage(generalMessages.name)} {...field} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="parking"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(payStationsMessages.parking)}
                        </FormLabel>
                        <FormControlStack className="mr-3">
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value} disabled={isEditModal}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={formatMessage(generalMessages.selectValue)} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {parkingsData?.results.map((parking) => (
                                            <SelectItem key={parking.id} value={parking.id}>
                                                {parking.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="paystation"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(cashCollectionsMessages.cashCollectionsPayStation)}
                        </FormLabel>
                        <FormControlStack className="mr-3">
                            <FormControl>
                                <Select
                                    onValueChange={field.onChange}
                                    value={field.value}
                                    disabled={!form.watch('parking')}
                                >
                                    <SelectTrigger className="w-full">
                                        <SelectValue placeholder={formatMessage(generalMessages.selectValue)} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {payStationsData?.results?.map((station) => (
                                            <SelectItem key={station.id} value={station.id}>
                                                {station.identifier}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            {hideStatusFormField && (
                <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                        <FormItem variant="dialog">
                            <FormLabel variant="dialog" required>
                                {formatMessage(cashCollectionsMessages.cashCollectionStatus)}
                            </FormLabel>
                            <FormControlStack className="mr-3">
                                <FormControl>
                                    <Select onValueChange={field.onChange} value={field.value}>
                                        <SelectTrigger className="w-full">
                                            <SelectValue placeholder={formatMessage(generalMessages.selectValue)} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(statusTranslationMap).map(([key, value]) => (
                                                <SelectItem key={key} value={key}>
                                                    {value}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </FormControl>
                                <FormMessage />
                            </FormControlStack>
                        </FormItem>
                    )}
                />
            )}
            <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">
                            {formatMessage(cashCollectionsMessages.collectionDescription)}
                        </FormLabel>
                        <FormControlStack className="mr-3">
                            <FormControl>
                                <Textarea
                                    placeholder={formatMessage(cashCollectionsMessages.collectionDescription)}
                                    {...field}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />

            <FormItem variant="dialog">
                <FormLabel variant="dialog" required>
                    {formatMessage(cashCollectionsMessages.cashCollectionDateTime)}
                </FormLabel>

                <div className="mr-4 flex w-full gap-2">
                    <div className="flex-1">
                        <FormField
                            control={form.control}
                            name="date"
                            render={({ field }) => {
                                const today = new Date();
                                const selectedDate = field.value ? new Date(field.value) : today;
                                return (
                                    <FormItem variant="dialog">
                                        <Popover open={openCalendar} onOpenChange={setOpenCalendar}>
                                            <PopoverTrigger asChild>
                                                <Button
                                                    id="date"
                                                    variant="outline"
                                                    className="text-base font-normal"
                                                    onClick={() => setOpenCalendar(true)}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faCalendar}
                                                        className="text-primary-700 mr-1 size-4"
                                                    />
                                                    {formatDate(selectedDate)}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverPortal>
                                                <PopoverContent className="z-50 bg-white p-4 shadow-sm" align="end">
                                                    <Calendar
                                                        mode="single"
                                                        selected={selectedDate}
                                                        onSelect={(date) => {
                                                            if (date) {
                                                                field.onChange(date.toISOString());
                                                                setOpenCalendar(false);
                                                            }
                                                        }}
                                                    />
                                                </PopoverContent>
                                            </PopoverPortal>
                                        </Popover>
                                    </FormItem>
                                );
                            }}
                        />
                    </div>
                    <div className="flex-1">
                        <FormField
                            control={form.control}
                            name="time"
                            render={({ field }) => <Input type="time" step={60} {...field} />}
                        />
                    </div>
                </div>
            </FormItem>

            {getFileUploadFormFieldBlock({
                fieldName: 'files',
                fieldLabel: cashCollectionsMessages.evidence,
            })}
        </>
    );
};

export default CollectionDetails;
